import { Instagram } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMarketplaceEnabled } from "@/lib/i18n";
import { CookiePreferencesButton } from "@/components/CookieConsent";
import { PwaInstallPrompt } from "@/components/PwaInstallPrompt";
import { RuixenGradientFooter } from "@/components/ui/ruixen-gradient-footer";

const linkCols = [
  {
    title: "Explorar",
    links: [
      { label: "Página inicial", href: "/" },
      { label: "Discos de vinil clássicos", href: "/discos-de-vinil" },
      { label: "Alternativa brasileira do Discogs", href: "/alternativa-ao-discogs" },
      { label: "Blog", href: "/blog" },
      { label: "Guias para colecionadores", href: "/guias" },
      { label: "Marketplace", href: "/marketplace" },
      { label: "Sugerir funcionalidades", href: "/lab", isNew: true },
    ],
  },
  {
    title: "Links",
    links: [
      { label: "Sobre nós", href: "/sobre-nos" },
      { label: "Contato", href: "/contato" },
      { label: "Política de reembolso", href: "/politica-de-reembolso" },
      { label: "Privacidade", href: "/privacidade" },
      { label: "Termos de serviço", href: "/termos-de-servico" },
    ],
  },
];

type FooterLink = { label: string; href: string; isNew?: boolean };

export function SiteFooter() {
  const marketplaceEnabled = useMarketplaceEnabled();

  return (
    <RuixenGradientFooter
      gradientHeight="32vh"
      minReveal={0.03}
      className="relative z-10 w-full border-t border-border bg-white text-black"
    >
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-16 sm:py-20 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-16">
          {/* Brand block */}
          <div className="md:col-span-1">
            <h2 className="max-w-[18ch] text-balance text-2xl font-medium leading-[1.1] tracking-tight sm:text-3xl lg:text-4xl">
              Sua coleção de vinil, viva e compartilhada.
            </h2>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-gray-500">
              Uma comunidade feita para quem curte discos de verdade.
            </p>
            <div className="mt-8">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                Baixe o app
              </p>
              <PwaInstallPrompt />
            </div>
          </div>

          {linkCols.map((col) => (
            <div key={col.title}>
              <div className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                {col.title}
              </div>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:block md:space-y-3">
                {(col.links as FooterLink[])
                  .filter((l) => marketplaceEnabled || l.href !== "/marketplace")
                  .map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className={cn(
                        "group inline-flex items-center gap-2 text-sm text-black transition-colors hover:text-gray-600",
                        l.isNew && "relative"
                      )}
                    >
                      <span className="relative">
                        {l.label}
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-current transition-all group-hover:w-full" />
                      </span>
                      {l.isNew && (
                        <span className="shrink-0 rounded-full bg-black px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                          Novo
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative z-10 border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-6 text-center text-xs text-gray-500 sm:flex-row sm:text-left md:px-10 lg:px-16">
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
            <span>{`© ${new Date().getFullYear()} Viny Club. Todos os direitos reservados.`}</span>
            <CookiePreferencesButton />
          </div>
          <a
            href="https://www.instagram.com/viny.club/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram da Viny Club"
            className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 transition-colors hover:border-black/30 hover:text-black"
          >
            <Instagram className="h-4 w-4" />
            <span>@viny.club</span>
          </a>
        </div>
      </div>
    </RuixenGradientFooter>
  );
}
